/**
 * DevModeContext - Development mode state for testing features
 *
 * Auth Flow (Server-Session Pattern):
 * 1. SSO authenticates user via iMIS → Gateway sets session cookie
 * 2. Cookie `session` contains JSON: {"session_id": "abc123", ...}
 * 3. On page load, app reads cookie and fetches profile from API
 * 4. API validates session with Gateway (or uses demo profile)
 * 5. Profile stored in React state for personalized responses
 *
 * Demo session IDs: demo-01, demo-02, demo-03 (recognized by backend)
 *
 * Reference: docs/MEMBER_AUTH_AND_BLOCKS.md
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchAuthProfile } from '../lib/api';
import { getClientIdFromPath } from '../lib/utils';

const DevModeContext = createContext(null);

// Cookie name - matches what SSO sets in production
// SSO sets a JSON cookie: {"session_id": "abc123", ...}
const AUTH_COOKIE_NAME = 'session';

// Demo session IDs (map to profiles in backend auth/demo_profiles.json)
// These are organized by client - frontend will filter by clientId
const AVAILABLE_SESSIONS = [
  // CPANB demo profiles
  {
    id: 'demo-01',
    label: 'Alexandra Spinner',
    description: 'FCPA, FCA - Crowe Soberman LLP (Member)',
    clientId: 'cpanb',
  },
  {
    id: 'demo-02',
    label: 'Michael Chen',
    description: 'CPA, CA - TechCorp Canada (Admin)',
    clientId: 'cpanb',
  },
  {
    id: 'demo-03',
    label: 'Dr. Faith Matchett',
    description: 'FCPA, FCMA - Associate Faculty RRU (Member)',
    clientId: 'cpanb',
  },
  // APA (American Psychiatric Association) demo profiles
  {
    id: 'demo-apa-01',
    label: 'Dr. Sarah Chen',
    description: 'MD, FAPA - Child & Adolescent Psychiatry (Fellow)',
    clientId: 'apa',
  },
  {
    id: 'demo-apa-02',
    label: 'Dr. Marcus Williams',
    description: 'MD - PGY-3 Resident, UCLA (Resident)',
    clientId: 'apa',
  },
  {
    id: 'demo-apa-03',
    label: 'Dr. Patricia Rodriguez',
    description: 'MD, MBA - Addiction Psychiatry (Distinguished Fellow)',
    clientId: 'apa',
  },
  // ICA (International Carwash Association) demo profiles
  {
    id: 'demo-ica-01',
    label: 'Mike Thompson',
    description: "Owner/Operator - Thompson's Express Wash (Operator)",
    clientId: 'ica',
  },
  {
    id: 'demo-ica-02',
    label: 'Jennifer Park',
    description: 'Regional Director - CleanStar Holdings (Corporate)',
    clientId: 'ica',
  },
  {
    id: 'demo-ica-03',
    label: 'Robert Martinez',
    description: 'Sales Manager - AquaTech Equipment (Supplier)',
    clientId: 'ica',
  },
  // Invalid session for testing
  {
    id: 'demo-invalid',
    label: 'Invalid Session',
    description: 'Test 401 error handling',
    clientId: null,
  },
];

// Environment-based cookie domain configuration
// Reference: thoughtfabric-sso-reference/src/auth/shared/cookieUtils.ts
function getCookieDomain() {
  const hostname = window.location.hostname;

  // Local development: use .thoughtfabriclocal.ai for SSO testing
  if (hostname.includes('thoughtfabriclocal.ai')) {
    return '.thoughtfabriclocal.ai';
  }

  // Staging: use .staging.app.thoughtfabric.com
  if (hostname.includes('staging.app.thoughtfabric.com')) {
    return '.staging.app.thoughtfabric.com';
  }

  // Production: use .app.thoughtfabric.com
  if (hostname.includes('app.thoughtfabric.com')) {
    return '.app.thoughtfabric.com';
  }

  // Default (localhost): no domain restriction
  return '';
}

function getCookieOptions() {
  const domain = getCookieDomain();
  const isSecure = window.location.protocol === 'https:';

  return {
    path: '/',
    ...(domain && { domain }),
    // Secure and SameSite settings for cross-domain cookies
    ...(isSecure && { secure: true, sameSite: 'none' }),
  };
}

// Cookie helpers - SSO cookie is JSON encoded
function getSessionFromCookie() {
  const match = document.cookie.match(new RegExp('(^| )' + AUTH_COOKIE_NAME + '=([^;]+)'));
  if (!match) return null;

  try {
    const decoded = decodeURIComponent(match[2]);
    const parsed = JSON.parse(decoded);
    return parsed.session_id || null;
  } catch {
    return null;
  }
}

function setSessionCookie(sessionId, days = 1) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const options = getCookieOptions();

  // Match SSO cookie format: JSON object with session_id
  const value = encodeURIComponent(JSON.stringify({ session_id: sessionId }));

  // Build cookie string with domain-aware options
  let cookieStr = `${AUTH_COOKIE_NAME}=${value}; expires=${expires}; path=${options.path}`;
  if (options.domain) {
    cookieStr += `; domain=${options.domain}`;
  }
  if (options.secure) {
    cookieStr += '; Secure';
  }
  if (options.sameSite) {
    cookieStr += `; SameSite=${options.sameSite}`;
  } else {
    cookieStr += '; SameSite=Lax';
  }

  document.cookie = cookieStr;
}

function deleteSessionCookie() {
  const options = getCookieOptions();

  // Delete cookie with same domain settings
  let cookieStr = `${AUTH_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${options.path}`;
  if (options.domain) {
    cookieStr += `; domain=${options.domain}`;
  }

  document.cookie = cookieStr;
}

function deleteReturnUrlCookie() {
  const options = getCookieOptions();

  // Delete returnUrl cookie (used for SSO redirect)
  let cookieStr = `returnUrl=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${options.path}`;
  if (options.domain) {
    cookieStr += `; domain=${options.domain}`;
  }

  document.cookie = cookieStr;
}

export function DevModeProvider({ children }) {
  // Check for ?demo=true URL param to hide dev panels during recordings
  const urlParams = new URLSearchParams(window.location.search);
  const isDemoMode = urlParams.get('demo') === 'true';

  // Dev mode disabled if ?demo=true is present (for clean recordings)
  const isDevMode = import.meta.env.DEV && !isDemoMode;

  // Get clientId from URL path (same as ClientContext)
  const clientId = getClientIdFromPath();

  // Read auth session from cookie (same format as SSO cookie)
  const [authSessionId, setAuthSessionId] = useState(() => getSessionFromCookie());

  // Dev panel visibility (only in dev mode)
  const [panelVisible, setPanelVisible] = useState(() => {
    if (!isDevMode) return false;
    try {
      return localStorage.getItem('devmode_panel_visible') !== 'false';
    } catch {
      return true;
    }
  });

  // Profile state - fetched from backend
  const [profileData, setProfileData] = useState({
    profile: null,
    source: null,
    loading: false,
    error: null,
  });

  // Persist panel visibility
  useEffect(() => {
    if (!isDevMode) return;
    try {
      localStorage.setItem('devmode_panel_visible', panelVisible ? 'true' : 'false');
    } catch {
      // Ignore
    }
  }, [panelVisible, isDevMode]);

  // Fetch profile when auth session changes
  // Pass clientId for Gateway validation (not needed for demo sessions)
  useEffect(() => {
    if (!authSessionId) {
      setProfileData({ profile: null, source: null, loading: false, error: null });
      return;
    }

    let cancelled = false;

    async function loadProfile() {
      setProfileData((prev) => ({ ...prev, loading: true, error: null }));

      try {
        // Pass clientId for Gateway validation (demo sessions don't need it)
        const result = await fetchAuthProfile(authSessionId, clientId);
        if (!cancelled) {
          if (result.authenticated) {
            console.log(
              `[DevModeContext] Profile loaded from ${result.source}:`,
              result.profile?.user_name || result.profile?.person?.full_name,
            );
            setProfileData({
              profile: result.profile,
              source: result.source,
              loading: false,
              error: null,
            });
          } else {
            // Session invalid - clear cookie
            console.log('[DevModeContext] Session invalid, clearing cookie');
            deleteSessionCookie();
            setAuthSessionId(null);
            setProfileData({ profile: null, source: null, loading: false, error: null });
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[DevModeContext] Profile fetch error:', err);
          setProfileData({
            profile: null,
            source: null,
            loading: false,
            error: err.message,
          });
        }
      }
    }

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [authSessionId, clientId]);

  // Login: set cookie with demo session ID (matches SSO format)
  // This is for demo/dev mode only - real SSO uses loginWithSSO()
  const login = useCallback((sessionId) => {
    setSessionCookie(sessionId);
    setAuthSessionId(sessionId);
  }, []);

  // Logout: clear cookies and cache
  const logout = useCallback(async () => {
    deleteSessionCookie();
    deleteReturnUrlCookie(); // Clear returnUrl cookie to avoid stale redirects
    setAuthSessionId(null);

    // Clear server-side cache
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      await fetch(`${apiBaseUrl}/api/auth/cache/clear`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
    } catch (err) {
      console.warn('[DevModeContext] Failed to clear server cache:', err);
    }
  }, []);

  // SSO Login: get SSO URL from client config and redirect
  // Flow: 1) GET /api/config/{client_id} → returns sso_url
  //       2) Set returnUrl cookie for Gateway callback
  //       3) Redirect to SSO URL
  //       4) SSO authenticates → calls Gateway callback → sets session cookie → redirects back
  const loginWithSSO = useCallback(async () => {
    if (!clientId) {
      console.error('[DevModeContext] Cannot login without clientId');
      return;
    }

    try {
      // Step 1: Fetch client config to get SSO URL
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
      console.log('[DevModeContext] Fetching client config for SSO URL');
      const configResponse = await fetch(`${apiBaseUrl}/api/config/${clientId}`);

      if (!configResponse.ok) {
        const errorText = await configResponse.text();
        throw new Error(`Config API returned ${configResponse.status}: ${errorText}`);
      }

      const config = await configResponse.json();
      console.log('[DevModeContext] Client config received:', config);

      // Step 2: Check if SSO URL is configured
      if (!config.sso_url) {
        throw new Error(`SSO not configured for client "${clientId}". Contact administrator.`);
      }

      // Step 3: Set returnUrl cookie for Gateway to read after SSO callback
      // Gateway reads this cookie to know where to redirect after authentication
      const returnUrl = window.location.href;
      const cookieDomain = getCookieDomain();
      const returnUrlEncoded = encodeURIComponent(returnUrl);

      // Set returnUrl cookie (Gateway expects this)
      let returnUrlCookie = `returnUrl=${returnUrlEncoded}; path=/`;
      if (cookieDomain) {
        returnUrlCookie += `; domain=${cookieDomain}`;
      }
      document.cookie = returnUrlCookie;
      console.log('[DevModeContext] Set returnUrl cookie:', returnUrl);

      // Step 4: Redirect to SSO URL
      console.log('[DevModeContext] Redirecting to SSO:', config.sso_url);
      window.location.href = config.sso_url;
    } catch (err) {
      console.error('[DevModeContext] SSO login failed:', err);
      alert(`SSO login failed: ${err.message}`);
    }
  }, [clientId]);

  const togglePanel = useCallback(() => {
    setPanelVisible((prev) => !prev);
  }, []);

  const value = {
    isDevMode,
    panelVisible: isDevMode && panelVisible,
    togglePanel,

    // Auth state (read from cookie)
    authSessionId,
    isAuthenticated: !!profileData.profile,
    profile: profileData.profile,
    profileSource: profileData.source,
    profileLoading: profileData.loading,
    profileError: profileData.error,

    // Auth actions
    login, // Demo login (sets cookie directly)
    loginWithSSO, // Real SSO login (redirects to Gateway)
    logout,
    // Filter sessions by current clientId (show matching + universal sessions)
    availableSessions: AVAILABLE_SESSIONS.filter((s) => !s.clientId || s.clientId === clientId),

    // SSO info
    clientId,
  };

  return <DevModeContext.Provider value={value}>{children}</DevModeContext.Provider>;
}

export function useDevMode() {
  const context = useContext(DevModeContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      isDevMode: false,
      panelVisible: false,
      togglePanel: () => {},
      authSessionId: null,
      isAuthenticated: false,
      profile: null,
      profileSource: null,
      profileLoading: false,
      profileError: null,
      login: () => {},
      loginWithSSO: () => {},
      logout: () => {},
      availableSessions: [],
      clientId: null,
    };
  }
  return context;
}
